import { useQuery } from '@tanstack/react-query';
import cx from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { MouseEventHandler, useCallback, useMemo } from 'react';

import useLinkProps from '../../../hooks/use-link-props';
import getGroups from '../../../utils/react-query/query-options/get-groups';
import getVehicle from '../../../utils/react-query/query-options/get-vehicle';
import getCategoryBySlug from '../../pages/vehicle/utils/get-category-by-slug';
import Button from '../../ui/button';
import { addCurrencySign } from '../../ui/legacy/inputs/filters/price-range/utils/add-currency-sign';
import IconClose from '../../ui/icons/close-new-version';

type TSearchItem = {
  label?: string;
  value: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
  type: string;
};

const SearchItem = ({ label, value, onClick, type }: TSearchItem) => {
  return (
    <div
      className={cx(
        'relative inline-block shrink-0 whitespace-no-wrap',
        'rounded-sm',
        'text-base',
        'bg-bparts-100 text-white',
        'px-4 font-bold',
        'flex items-center gap-3',
        'capitalize',
        'truncate py-[5px]'
      )}
      data-cy={`search-badge-${type}`}
    >
      {label && <>{label}:</>}
      <span className={cx('font-bold w-full')}>{value}</span>
      <Button
        variant="ROUNDED-BLANK"
        type="button"
        isSmall={true}
        disableHeight={true}
        isFullWidth={false}
        className={cx(' group')}
        onClick={(e) => {
          e.preventDefault();
          onClick(e);
        }}
      >
        <IconClose
          className={cx(
            'h-[14px] lg:h-4',
            'w-[14px] lg:w-4 hover:text-bparts-100'
          )}
        />
      </Button>
    </div>
  );
};

const ReferenceItem = ({ value: reference }: Partial<TSearchItem>) => {
  const { t } = useTranslation();
  const getLink = useLinkProps();
  const { query, push } = useRouter();

  const onClick = () => {
    const { reference, ...queryToKeep } = query;
    const link = getLink('reference', queryToKeep);
    push(link.href, link.as, { shallow: true });
  };

  return (
    <SearchItem value={`${reference}`} onClick={onClick} type="reference" />
  );
};

const PriceItem = ({
  value: price,
  priceTemplate,
  queryKey,
}: Partial<TSearchItem> & {
  queryKey: 'priceFrom' | 'priceTo';
  priceTemplate?: string;
}) => {
  const { t } = useTranslation();
  const getLink = useLinkProps();
  const { query, push } = useRouter();

  const onClick = () => {
    const { priceFrom, priceTo, ...queryToKeep } = query;
    const link = getLink('reference', {
      ...queryToKeep,
      ...(queryKey === 'priceTo' && priceFrom && { priceFrom }),
      ...(queryKey === 'priceFrom' && priceTo && { priceTo }),
    });
    push(link.href, link.as, { shallow: true });
  };

  const priceWithCurrency = priceTemplate
    ? addCurrencySign(parseInt(price as string), [
        { label: priceTemplate, value: 0 },
      ])
    : priceTemplate;

  const label =
    queryKey === 'priceFrom'
      ? t('products:priceRange.from')
      : t('products:priceRange.to');

  return (
    <SearchItem
      value={`${label}: ${priceWithCurrency}`}
      onClick={onClick}
      type={queryKey}
    />
  );
};

const PartItem = ({
  category,
  subCategory,
}: {
  category: string;
  subCategory: string;
}) => {
  const getLink = useLinkProps();
  const { locale = 'en', query, push } = useRouter();

  const { data: groups } = useQuery(...getGroups({ locale }));

  const partName = useCallback(() => {
    const names = getCategoryBySlug(
      {
        category: category,
        subCategory: subCategory,
      },
      groups
    );

    return `${names.category} - ${names.subCategory}`;
  }, [category, subCategory, groups]);

  const onClick = () => {
    const { category, subCategory, ...queryToKeep } = query;
    const link = getLink('reference', queryToKeep);
    push(link.href, link.as, { shallow: true });
  };

  return <SearchItem value={`${partName()}`} onClick={onClick} type="part" />;
};

type SearchItemsProps = {
  priceTemplate?: string;
};

const SearchItems = ({ priceTemplate }: SearchItemsProps) => {
  const {
    query: { reference, category, subCategory, priceFrom, priceTo },
  } = useRouter();

  return (
    <div
      className={cx('lg:pt-4', 'flex', 'gap-4', 'inline-block overflow-y-auto')}
    >
      {reference && <ReferenceItem value={reference as string} />}
      {category && subCategory && (
        <PartItem
          category={category as string}
          subCategory={subCategory as string}
        />
      )}
      {priceFrom && (
        <PriceItem
          value={priceFrom as string}
          priceTemplate={priceTemplate}
          queryKey="priceFrom"
        />
      )}
      {priceTo && (
        <PriceItem
          value={priceTo as string}
          priceTemplate={priceTemplate}
          queryKey="priceTo"
        />
      )}
    </div>
  );
};

export default SearchItems;
